import React from 'react';
import queryString from 'query-string';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Link from '@/components/Link';
import { urls } from '@/constants';
import { SignIn as SignInDomain } from '@/domains';
import { translations } from '@/locale';
import { SessionConsumer } from '@/components/Context/Session';

import logo from '@/assets/logo.svg';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const formSchema = z.object({
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  password: z.string().min(1, 'Password is required')
});

type FormValues = z.infer<typeof formSchema>;

export interface Props extends RouteComponentProps {}

const SignInPage: React.FC<Props & WrappedComponentProps> = (props) => {
  const email = queryString.parse(location.search).email as string;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || '',
      password: ''
    }
  });

  return (
    <SessionConsumer>
      {({ signInWithEmail }) => (
        <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img className="mx-auto h-12 w-auto" src={logo} alt="Workflow" />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <Card>
              <CardHeader>
                <CardTitle>Welcome back</CardTitle>
                <CardDescription>Enter your credentials to access your account.</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit((values) => signInWithEmail(values.email, values.password))}
                    className="space-y-6"
                  >
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{props.intl.formatMessage({ id: translations.inputs.email.label })}</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={props.intl.formatMessage({ id: translations.inputs.email.placeholder })}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{props.intl.formatMessage({ id: translations.inputs.password.label })}</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder={props.intl.formatMessage({ id: translations.inputs.password.placeholder })}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex flex-row-reverse">
                      <Link
                        to={{
                          pathname: urls.forgotPassword.main,
                          search: queryString.stringify({ email: form.watch('email') })
                        }}
                        className="text-sm text-orange-600 hover:text-orange-500"
                      >
                        Forgot your password?
                      </Link>
                    </div>

                    <Button type="submit" className="w-full" disabled={form.formState.isSubmitting} variant="default">
                      {form.formState.isSubmitting ? 'Signing in...' : 'Sign in'}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </SessionConsumer>
  );
};

export default injectIntl(SignInPage);
